/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Video options presets
 * These are the base configurations that can be extended or overridden
 */
export const VIDEO_OPTIONS = {
  /**
   * Default configuration applied to ALL videos
   */
  default: {
    // Vimeo Parameters
    autopause: false,
    byline: false,
    height: 1080,
    portrait: false,
    title: false,
    transparent: true,

    // YouTube Parameters
    autohide: true,
    disablekb: true,
    modestbranding: true,
    rel: false,
    showinfo: false,

    // HTML (playsinline required for reliable inline playback on iOS Safari)
    controls: true,
    playsinline: true,

    // Custom Options
    size: 'cover',
    autopilot: true,
    readonly: true,
  },

  /**
   * Contains isolated options which will apply when enable `autopilot` option.
   * Options belong this preset can't be overridden by server options or other presets.
   *
   * `autopilot` option will auto turned on if video rendered as `readonly`.
   *
   * @readonly Do not extends or override this preset
   */
  autopilot: {
    // The `lazy` flag prevent initialize video player when create new video instance
    // The initialization will trigger by directive which controls video playback.
    lazy: true,

    // disable `autoplay` as video playback will controlled programmatically by other directive.
    autoplay: false,
    loop: true,
    mute: true,
    muted: true,
  },

  /**
   * Readonly preset usually used for video which has no controls, such as: background video, product tile video, etc..
   * When enabled, `autopilot` mode will force to enable as well to control the video playback as video rendered as `readonly`.
   *
   * **Note:**
   *  - Options belong this preset can't be overridden by server options or other presets, except `autopilot` preset.
   *  - `readonly` options will auto applied to video if `readonly` attribute exists on video element.
   *
   * @readonly Do not extends or override this preset
   * @example
   *
   * ```html
   *  <!-- without `readonly` attribute -->
   *  <div amb-video="16401445" data-options="readonly"></div>
   *
   *  <!-- or with `readonly` attribute -->
   *  <div amb-video="16401445" readonly></div>
   * ```
   */
  readonly: {
    controls: false,
  },

  /**
   * Hero video preset
   */
  hero: {
    autoplay: true,
    loop: true,
    mute: true,
    muted: true,
    controls: false,
    size: 'cover',
    readonly: true,
    autopilot: false,
  },

  /**
   * Interactive video preset
   */
  interactive: {
    controls: true,
    autoplay: false,
    loop: false,
    mute: false,
    muted: false,
    readonly: false,
    autopilot: false,
  },

  /**
   * Product tile preset
   */
  productTile: {
    controls: false,
    loop: true,
    mute: true,
    muted: true,
    autopilot: true,
    ratio: 1,
    size: 'cover',
  },

  /**
   * Product gallery preset
   */
  productGallery: {
    controls: true,
    autoplay: false,
    loop: false,
    autopilot: false,
    size: 'contain',
  },
};

/**
 * Autopilot behavior presets
 * Controls how videos behave when scrolling in/out of viewport
 */
export const VIDEO_AUTOPILOT_BEHAVIORS = {
  /**
   * Default: auto-init and autoplay when visible
   */
  default: {
    threshold: 0.25,
    enter: ['init', 'autoplay'],
    leave: ['autopause'],
  },

  /**
   * Resume: remembers user playback state
   */
  resume: {
    threshold: 0.5,
    enter: ['init', 'resume'],
    leave: ['autopause'],
  },

  /**
   * Lazy: only loads metadata, no autoplay
   */
  lazy: {
    threshold: 0.1,
    enter: ['init'],
    leave: [],
  },

  /**
   * Aggressive: plays immediately when any part visible
   */
  aggressive: {
    threshold: 0,
    enter: ['init', 'autoplay'],
    leave: ['autopause'],
  },

  /**
   * No autopilot
   */
  manual: false,
};
